import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { DataTable } from '@/components/DataTable';
import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  Modal,
  PageHeader,
  Toast,
} from '@/components/ui';
import { formatDateTime, formatPhone, titleCase } from '@/lib/format';

interface DncEntry {
  id: string;
  phone: string;
  scope: string;
  source: string;
  reason: string | null;
  pushed_to_vicidial: boolean;
  pushed_at: string | null;
  created_at: string;
}

interface Callback {
  id: string;
  phone: string;
  scheduled_at: string;
  status: string;
  notes: string | null;
  pushed_to_vicidial: boolean;
  bot_name: string | null;
  created_at: string;
}

export default function Compliance() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const add = useMutation({
    mutationFn: () => api.post('/dnc', { phone, reason: reason || null, scope: 'TENANT' }),
    onSuccess: () => {
      setOpen(false);
      setPhone('');
      setReason('');
      queryClient.invalidateQueries({ queryKey: ['dnc'] });
      setToast({ message: 'Number suppressed', tone: 'good' });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Could not add'),
        tone: 'bad',
      }),
  });

  return (
    <div className="w-full">
      <PageHeader
        title="DNC & callbacks"
        description="Suppression list and scheduled call-backs."
        actions={
          can('dnc:create') && <Button onClick={() => setOpen(true)}>Add to DNC</Button>
        }
      />

      <Card className="mb-4">
        <p className="text-sm text-ink-600 dark:text-ink-300">
          When a customer asks not to be called, the number is suppressed here{' '}
          <strong className="text-ink-900 dark:text-ink-50">immediately</strong> — before any
          attempt to reach the dialer — then mirrored to Vicidial in the background. A
          suppressed number is refused at call start even if the dialer hands one over, and a
          do-not-call request is never transferred to a human.
        </p>
      </Card>

      <div className="space-y-6">
        <DataTable<DncEntry>
          title="Suppression list"
          queryKey="dnc"
          path="/dnc"
          searchPlaceholder="Phone number…"
          emptyTitle="Nothing suppressed"
          emptyDescription="Numbers land here when a customer asks not to be called, or when you add one manually."
          columns={[
            {
              key: 'phone',
              header: 'Number',
              render: (entry) => (
                <span className="font-mono text-sm">{formatPhone(entry.phone)}</span>
              ),
            },
            {
              key: 'scope',
              header: 'Scope',
              render: (entry) => (
                <Badge tone={entry.scope === 'GLOBAL' ? 'red' : 'gray'}>{entry.scope}</Badge>
              ),
            },
            {
              key: 'source',
              header: 'Source',
              render: (entry) => titleCase(entry.source),
            },
            {
              key: 'reason',
              header: 'Reason',
              render: (entry) => (
                <span className="text-xs text-ink-600 dark:text-ink-300">
                  {entry.reason ?? '—'}
                </span>
              ),
            },
            {
              key: 'pushed',
              header: 'In Vicidial',
              render: (entry) =>
                entry.pushed_to_vicidial ? (
                  <Badge tone="green">Mirrored</Badge>
                ) : (
                  <Badge tone="amber">Pending</Badge>
                ),
            },
            {
              key: 'created_at',
              header: 'Added',
              render: (entry) => (
                <span className="text-xs text-ink-500">{formatDateTime(entry.created_at)}</span>
              ),
            },
          ]}
        />

        <DataTable<Callback>
          title="Scheduled callbacks"
          queryKey="callbacks"
          path="/callbacks"
          searchable={false}
          emptyTitle="No callbacks scheduled"
          emptyDescription="A callback is created when a customer asks to be called at a better time."
          filters={[
            {
              key: 'status',
              label: 'Status',
              options: ['SCHEDULED', 'PUSHED', 'COMPLETED', 'CANCELLED', 'FAILED'].map(
                (value) => ({ value, label: titleCase(value) }),
              ),
            },
          ]}
          columns={[
            {
              key: 'phone',
              header: 'Number',
              render: (callback) => (
                <span className="font-mono text-sm">{formatPhone(callback.phone)}</span>
              ),
            },
            {
              key: 'scheduled_at',
              header: 'Scheduled for',
              render: (callback) => formatDateTime(callback.scheduled_at),
            },
            {
              key: 'bot',
              header: 'Bot',
              render: (callback) => callback.bot_name ?? <span className="text-ink-400">—</span>,
            },
            {
              key: 'status',
              header: 'Status',
              render: (callback) => (
                <Badge tone={callback.status === 'SCHEDULED' ? 'amber' : 'green'}>
                  {titleCase(callback.status)}
                </Badge>
              ),
            },
            {
              key: 'notes',
              header: 'Notes',
              render: (callback) => (
                <span className="text-xs text-ink-600 dark:text-ink-300">
                  {callback.notes ?? '—'}
                </span>
              ),
            },
          ]}
        />
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Add to suppression list"
        description="This number will never be called again by any of your bots."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button disabled={!phone} loading={add.isPending} onClick={() => add.mutate()}>
              Suppress
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Phone number" required>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="5555550100"
            />
          </Field>
          <Field label="Reason" hint="Optional, for your own records.">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
